/**
 * Airport manager — unified searchable Detect + manual ICAO list.
 *
 * One row per ICAO (no duplicates). Jet A edits write through the same
 * `jetAOverrides[]` path Settings uses (no second fuel DB). Avgas is
 * optional per-airport and stored here only.
 *
 * Runway data extends beyond the single `runwayFt` to multi-runway with
 * length + TODA. Weight restrictions per airport feed #11 landing-weight
 * cross-check. Curfew / customs editorial fields surface #8 / #12 data
 * on the same record.
 */
import { lookupAirport, hydrateSceneryAirport, resolveCoords } from "./airports";
import { resolveAirportRunway } from "./airportRunways";
import { lookupAirportCoords } from "./airportCoords";
import { hoursForAirport, type AirportHours } from "./curfew";
import { safeTrim } from "./text";
import type { SceneryAirport, SceneryPresence } from "./types";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type AirportRunwayEntry = {
  id: string;
  lengthFt: number;
  todaFt: number;
  surface?: string;
  ident?: string;
};

export type AirportWeightRestriction = {
  maxLandingWeightLb: number;
  maxTakeoffWeightLb: number;
  note: string;
};

export type AirportManagerRecord = {
  icao: string;
  iata: string;
  name: string;
  city: string;
  country: string;
  lat?: number;
  lon?: number;
  /** Legacy single-runway value kept for backward compat. */
  runwayFt: number;
  /** Multi-runway data — empty until user edits or data is seeded. */
  runways: AirportRunwayEntry[];
  /** Jet A price per US gal — syncs with jetAOverrides. */
  jetAPricePerGal?: number;
  /** Optional Avgas price per US gal (Jet A remains planning default). */
  avgasPricePerGal?: number;
  /** Structural / regulatory weight limits at this airport. */
  weightRestriction?: AirportWeightRestriction;
  /** User-editable curfew / quiet-hours note. Complements curfew.ts built-in. */
  curfewNote: string;
  /** True when the user confirmed this field has customs. */
  hasCustoms?: boolean;
  /** User-editable customs notes. */
  customsNote: string;
  /** Free-form ops notes. */
  opsNotes: string;
  /** Origin: 'detect' (community scan), 'manual' (hand-added), 'catalog' (built-in). */
  source: "detect" | "manual" | "catalog";
  notes: string;
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

export function icaoKey(icao?: string | null): string {
  return safeTrim(icao).toUpperCase();
}

export function newRunwayId(): string {
  return `rwy-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export function blankRunway(): AirportRunwayEntry {
  return { id: newRunwayId(), lengthFt: 0, todaFt: 0 };
}

export function hydrateRunway(raw: unknown): AirportRunwayEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Record<string, unknown>;
  const lengthFt = Number(src.lengthFt);
  const todaFt = Number(src.todaFt);
  if (!Number.isFinite(lengthFt) || lengthFt <= 0) return null;
  return {
    id: typeof src.id === "string" && src.id.trim() ? src.id.trim() : newRunwayId(),
    lengthFt: Math.round(lengthFt),
    todaFt: Number.isFinite(todaFt) && todaFt > 0 ? Math.round(todaFt) : Math.round(lengthFt),
    surface: typeof src.surface === "string" ? src.surface.trim() : undefined,
    ident: typeof src.ident === "string" ? src.ident.trim() : undefined,
  };
}

export function hydrateRunways(raw: unknown): AirportRunwayEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: AirportRunwayEntry[] = [];
  for (const item of raw) {
    const entry = hydrateRunway(item);
    if (entry) out.push(entry);
  }
  return out;
}

export function hydrateWeightRestriction(raw: unknown): AirportWeightRestriction | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const src = raw as Record<string, unknown>;
  const mlw = Number(src.maxLandingWeightLb);
  const mtow = Number(src.maxTakeoffWeightLb);
  if ((!Number.isFinite(mlw) || mlw <= 0) && (!Number.isFinite(mtow) || mtow <= 0)) return undefined;
  return {
    maxLandingWeightLb: Number.isFinite(mlw) && mlw > 0 ? Math.round(mlw) : 0,
    maxTakeoffWeightLb: Number.isFinite(mtow) && mtow > 0 ? Math.round(mtow) : 0,
    note: typeof src.note === "string" ? src.note.trim().slice(0, 200) : "",
  };
}

/** Longest runway from the multi-runway array, or fallback to legacy `runwayFt`. */
export function longestRunwayFt(rec: Pick<AirportManagerRecord, "runways" | "runwayFt">): number {
  if (rec.runways.length === 0) return rec.runwayFt;
  return Math.max(rec.runwayFt, ...rec.runways.map((r) => r.lengthFt));
}

/* ------------------------------------------------------------------ */
/*  Hydrate / build                                                    */
/* ------------------------------------------------------------------ */

export function hydrateAirportManagerRecord(raw: unknown): AirportManagerRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Record<string, unknown>;
  const code = icaoKey(src.icao as string);
  if (!code || code.length < 3 || code.length > 4) return null;
  return {
    icao: code,
    iata: typeof src.iata === "string" ? src.iata.trim().toUpperCase().slice(0, 3) : "",
    name: typeof src.name === "string" ? src.name.trim() : code,
    city: typeof src.city === "string" ? src.city.trim() : "",
    country: typeof src.country === "string" ? src.country.trim() : "",
    lat: typeof src.lat === "number" && Number.isFinite(src.lat) ? src.lat : undefined,
    lon: typeof src.lon === "number" && Number.isFinite(src.lon) ? src.lon : undefined,
    runwayFt: Number.isFinite(Number(src.runwayFt)) && Number(src.runwayFt) > 0 ? Math.round(Number(src.runwayFt)) : 0,
    runways: hydrateRunways(src.runways),
    jetAPricePerGal: typeof src.jetAPricePerGal === "number" && Number.isFinite(src.jetAPricePerGal) && src.jetAPricePerGal > 0
      ? Math.round(src.jetAPricePerGal * 100) / 100
      : undefined,
    avgasPricePerGal: typeof src.avgasPricePerGal === "number" && Number.isFinite(src.avgasPricePerGal) && src.avgasPricePerGal > 0
      ? Math.round(src.avgasPricePerGal * 100) / 100
      : undefined,
    weightRestriction: hydrateWeightRestriction(src.weightRestriction),
    curfewNote: typeof src.curfewNote === "string" ? src.curfewNote.trim().slice(0, 500) : "",
    hasCustoms: src.hasCustoms === true ? true : src.hasCustoms === false ? false : undefined,
    customsNote: typeof src.customsNote === "string" ? src.customsNote.trim().slice(0, 500) : "",
    opsNotes: typeof src.opsNotes === "string" ? src.opsNotes.trim().slice(0, 1000) : "",
    source: src.source === "detect" || src.source === "manual" || src.source === "catalog"
      ? src.source
      : "manual",
    notes: typeof src.notes === "string" ? src.notes.trim() : "",
  };
}

export function hydrateAirportManagerRecords(raw: unknown): AirportManagerRecord[] {
  if (!Array.isArray(raw)) return [];
  const out: AirportManagerRecord[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const rec = hydrateAirportManagerRecord(item);
    if (!rec || seen.has(rec.icao)) continue;
    seen.add(rec.icao);
    out.push(rec);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  Build unified list from Destinations + Detect inventory            */
/* ------------------------------------------------------------------ */

export function recordFromScenery(
  entry: SceneryAirport,
  inventory: SceneryPresence[],
): AirportManagerRecord {
  const code = icaoKey(entry.icao);
  const catalogAirport = lookupAirport(code);
  const resolved = resolveAirportRunway(code, { runwayFt: entry.runwayFt, catalogFt: catalogAirport?.runwayFt });
  const coords = resolveCoords(code, [entry]);
  const isDetect = inventory.some((row) => icaoKey(row.icao) === code);
  return {
    icao: code,
    iata: catalogAirport?.iata ?? "",
    name: entry.name || catalogAirport?.name || code,
    city: entry.city || catalogAirport?.city || "",
    country: entry.country || catalogAirport?.country || "",
    lat: entry.lat ?? coords?.lat,
    lon: entry.lon ?? coords?.lon,
    runwayFt: resolved.longestFt,
    runways: [],
    curfewNote: "",
    customsNote: "",
    opsNotes: "",
    source: isDetect ? "detect" : "manual",
    notes: entry.notes || "",
  };
}

/**
 * Build the unified Airport Manager list from:
 * - existing persisted `airportManager` records
 * - `scenery` (Destinations allow-list)
 * - `inventory` (Community Detect scan)
 *
 * One row per ICAO. Persisted Airport Manager data wins over Destinations.
 * Detect ICAOs not already in destinations or persisted records get a stub.
 */
export function buildUnifiedAirportList(
  persisted: AirportManagerRecord[],
  scenery: SceneryAirport[],
  inventory: SceneryPresence[],
): AirportManagerRecord[] {
  const byIcao = new Map<string, AirportManagerRecord>();

  for (const rec of persisted) {
    byIcao.set(rec.icao, rec);
  }

  for (const entry of scenery) {
    const code = icaoKey(entry.icao);
    if (!code || byIcao.has(code)) continue;
    byIcao.set(code, recordFromScenery(entry, inventory));
  }

  for (const row of inventory) {
    const code = icaoKey(row.icao);
    if (!code || byIcao.has(code)) continue;
    const catalogAirport = lookupAirport(code);
    const resolved = resolveAirportRunway(code, { catalogFt: catalogAirport?.runwayFt });
    const coords = lookupAirportCoords(code);
    byIcao.set(code, {
      icao: code,
      iata: catalogAirport?.iata ?? "",
      name: catalogAirport?.name || code,
      city: catalogAirport?.city || "",
      country: catalogAirport?.country || "",
      lat: catalogAirport?.lat ?? coords?.lat,
      lon: catalogAirport?.lon ?? coords?.lon,
      runwayFt: resolved.longestFt,
      runways: [],
      curfewNote: "",
      customsNote: "",
      opsNotes: "",
      source: "detect",
      notes: "",
    });
  }

  return [...byIcao.values()].sort((a, b) => a.icao.localeCompare(b.icao));
}

/* ------------------------------------------------------------------ */
/*  Search                                                             */
/* ------------------------------------------------------------------ */

export function airportMatchesQuery(rec: AirportManagerRecord, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    rec.icao.toLowerCase().includes(q) ||
    rec.iata.toLowerCase().includes(q) ||
    rec.name.toLowerCase().includes(q) ||
    rec.city.toLowerCase().includes(q) ||
    rec.country.toLowerCase().includes(q)
  );
}

/* ------------------------------------------------------------------ */
/*  Upsert / remove                                                    */
/* ------------------------------------------------------------------ */

export function upsertAirportManagerRecord(
  list: AirportManagerRecord[],
  updated: AirportManagerRecord,
): AirportManagerRecord[] {
  const code = icaoKey(updated.icao);
  if (!code) return list;
  const idx = list.findIndex((r) => r.icao === code);
  if (idx === -1) {
    return [...list, { ...updated, icao: code }].sort((a, b) => a.icao.localeCompare(b.icao));
  }
  return list.map((r, i) => (i === idx ? { ...updated, icao: code } : r));
}

export function removeAirportManagerRecord(
  list: AirportManagerRecord[],
  icao: string,
): AirportManagerRecord[] {
  const code = icaoKey(icao);
  return list.filter((r) => r.icao !== code);
}

/* ------------------------------------------------------------------ */
/*  #11 — Landing weight cross-check                                   */
/* ------------------------------------------------------------------ */

export type LandingWeightCheck = {
  estimatedLandingWeightLb: number;
  airportMaxLandingWeightLb: number;
  marginLb: number;
  overweightLb: number;
  ok: boolean;
  note: string;
};

const LANDING_WEIGHT_SAFETY_MARGIN_FACTOR = 1.02;

/**
 * Cross-check derived landing weight vs destination airport restrictions.
 *
 * `etw` is SimBrief estimated takeoff weight (lb).
 * `tripBurnLb` is SimBrief calculated fuel burn for the trip (lb).
 *
 * Landing weight ≈ ETW − trip burn, with a 2% safety margin on the result.
 * Flag when the aircraft would be too heavy to land legally.
 */
export function checkLandingWeight(
  etw: number,
  tripBurnLb: number,
  destAirport: Pick<AirportManagerRecord, "weightRestriction"> | undefined | null,
): LandingWeightCheck {
  const estLandingRaw = etw - tripBurnLb;
  const estLanding = Math.round(estLandingRaw * LANDING_WEIGHT_SAFETY_MARGIN_FACTOR);
  const airportLimit = destAirport?.weightRestriction?.maxLandingWeightLb ?? 0;

  if (airportLimit <= 0) {
    return {
      estimatedLandingWeightLb: estLanding,
      airportMaxLandingWeightLb: 0,
      marginLb: 0,
      overweightLb: 0,
      ok: true,
      note: "No airport landing-weight restriction on file.",
    };
  }

  const overweight = estLanding - airportLimit;
  return {
    estimatedLandingWeightLb: estLanding,
    airportMaxLandingWeightLb: airportLimit,
    marginLb: airportLimit - estLanding,
    overweightLb: Math.max(0, overweight),
    ok: overweight <= 0,
    note: overweight > 0
      ? `OVERWEIGHT  est landing ${estLanding.toLocaleString()} lb exceeds ${destAirport!.weightRestriction!.note || "airport"} limit ${airportLimit.toLocaleString()} lb by ${overweight.toLocaleString()} lb`
      : `Landing weight ${estLanding.toLocaleString()} lb within ${airportLimit.toLocaleString()} lb limit (margin ${(airportLimit - estLanding).toLocaleString()} lb)`,
  };
}

export type LandingWeightOfp = {
  fuelPlan?: { trip?: number; units?: string };
  weights?: { estTow?: number; estLdw?: number; units?: string };
};

/** OFP takeoff weight and trip burn in lb, or null when the OFP carries no aircraft weight. */
export function ofpTakeoffWeightAndTrip(ofp: LandingWeightOfp | null | undefined): { etwLb: number; tripBurnLb: number } | null {
  const trip = ofp?.fuelPlan?.trip;
  if (!trip || trip <= 0) return null;
  const tow = ofp?.weights?.estTow ?? (ofp?.weights?.estLdw ? ofp.weights.estLdw + trip : undefined);
  if (!tow || tow <= trip) return null;
  const units = (ofp?.weights?.units || ofp?.fuelPlan?.units || "").toLowerCase();
  const toLb = units === "kgs" || units === "kg" ? 2.20462 : 1;
  return { etwLb: tow * toLb, tripBurnLb: trip * toLb };
}

/**
 * Derive landing weight from the SimBrief OFP's estimated takeoff weight and
 * cross-check against destination. Returns null when the OFP lacks a weight.
 */
export function landingWeightFromOfp(
  ofp: LandingWeightOfp | null | undefined,
  destAirport: Pick<AirportManagerRecord, "weightRestriction"> | undefined | null,
  aircraftMlwLb?: number,
): LandingWeightCheck | null {
  const masses = ofpTakeoffWeightAndTrip(ofp);
  if (!masses) return null;
  const { etwLb, tripBurnLb } = masses;

  const check = checkLandingWeight(etwLb, tripBurnLb, destAirport);

  if (check.ok && aircraftMlwLb && aircraftMlwLb > 0 && check.estimatedLandingWeightLb > aircraftMlwLb) {
    return {
      ...check,
      ok: false,
      overweightLb: check.estimatedLandingWeightLb - aircraftMlwLb,
      note: `OVERWEIGHT  est landing ${check.estimatedLandingWeightLb.toLocaleString()} lb exceeds aircraft MLW ${aircraftMlwLb.toLocaleString()} lb by ${(check.estimatedLandingWeightLb - aircraftMlwLb).toLocaleString()} lb`,
    };
  }

  return check;
}

/* ------------------------------------------------------------------ */
/*  #12 — Customs lookup                                               */
/* ------------------------------------------------------------------ */

export function airportHasCustoms(rec: AirportManagerRecord | undefined | null): boolean {
  return rec?.hasCustoms === true;
}

export function findNearestCustomsAirport(
  destIcao: string,
  airportList: AirportManagerRecord[],
): AirportManagerRecord | null {
  const destCoords = resolveCoords(destIcao);
  if (!destCoords) return null;
  let best: AirportManagerRecord | null = null;
  let bestDist = Infinity;
  for (const ap of airportList) {
    if (!ap.hasCustoms || ap.icao === icaoKey(destIcao)) continue;
    if (ap.lat == null || ap.lon == null) continue;
    const dlat = ap.lat - destCoords.lat;
    const dlon = ap.lon - destCoords.lon;
    const dist = dlat * dlat + dlon * dlon;
    if (dist < bestDist) {
      bestDist = dist;
      best = ap;
    }
  }
  return best;
}

/* ------------------------------------------------------------------ */
/*  #8 — Curfew lookup with user overrides                             */
/* ------------------------------------------------------------------ */

export function curfewForAirport(
  icao: string,
  airportList: AirportManagerRecord[],
): { hours: AirportHours; userNote: string } {
  const code = icaoKey(icao);
  const hours = hoursForAirport(code);
  const rec = airportList.find((r) => r.icao === code);
  return { hours, userNote: rec?.curfewNote ?? "" };
}
